import React, { useMemo } from 'react';

import { Conversation, ConversationContent, ConversationScrollButton } from '@/ai-elements/conversation';
import { Shimmer } from '@/ai-elements/shimmer';
import { VoiceWidgetInTextChat } from '../../components/VoiceWidgetInTextChat';
import { useIsCompactVoiceLayout } from '../hooks/useIsCompactVoiceLayout';
import type { VoiceTranscriptLine, VoiceWidgetTimedEvent } from '../hooks/useVoiceSession';
import { VoiceTranscriptBubble } from './VoiceTranscriptBubble';

/**
 * Wraps the call's transcript lines and widget events into a single
 * chronological scroll (decisions/078). Replaces the two-pane
 * intro/chatting/widget layout from D-52/D-54.
 *
 * Each transcript line and each widget event becomes a row. Rows are
 * sorted by `createdAt`, which is the wall-clock at which the FE
 * received the event — close enough to the model's narrative order to
 * be the right user-facing chronology (we'd need server-side
 * monotonic sequencing to do strictly better; not worth it for v1).
 *
 * Widgets are rendered via the existing `<VoiceWidgetInTextChat>`
 * adapter (its dispatch table covers all 13 voice widget types) so the
 * live overlay and the post-end text chat share one render path. The
 * adapter also centers widgets horizontally per decisions/078.
 *
 * Sticks to the bottom while new items arrive or a partial transcript
 * line grows (via `use-stick-to-bottom`, same as the text chat). When the
 * user scrolls up, auto-stick pauses and the text chat's floating
 * scroll-to-bottom arrow (`ConversationScrollButton`) appears.
 */
export interface VoiceScrollListProps {
  lines: VoiceTranscriptLine[];
  widgetEvents: VoiceWidgetTimedEvent[];
  /**
   * Forwarded to `<VoiceWidgetInTextChat>` so an inline `prompt-login`
   * widget can fire Cognito callbacks during a live call. The post-end
   * text chat (which uses this same adapter via ConciergePartItem) omits
   * it and the widget renders read-only.
   */
  onLoginCompleted?: () => void;
  onLoginFailed?: (reason: string) => void;
  /**
   * Latest tool-call status text (D-89). Rendered as a shimmering line below
   * the most recent message, mirroring the text chat's "Thinking…" status
   * (see `ChatMessage` shimmer). Null/empty hides the row.
   */
  pendingStatus?: string | null;
  /**
   * True when the overlay renders `VoiceBottomDock` (blob enabled). Raises
   * the scroll-to-bottom arrow above the 130px dock fade; without the dock
   * the arrow sits at the text chat's default 16px offset.
   */
  hasBottomDock?: boolean;
}

type ScrollItem =
  | { kind: 'line'; key: string; createdAt: number; line: VoiceTranscriptLine }
  | { kind: 'widget'; key: string; createdAt: number; event: VoiceWidgetTimedEvent };

export const VoiceScrollList: React.FC<VoiceScrollListProps> = ({
  lines,
  widgetEvents,
  onLoginCompleted,
  onLoginFailed,
  pendingStatus,
  hasBottomDock,
}) => {
  const isCompact = useIsCompactVoiceLayout();
  const liveLoginCallbacks = useMemo(() => ({ onLoginCompleted, onLoginFailed }), [onLoginCompleted, onLoginFailed]);

  const items = useMemo<ScrollItem[]>(() => {
    const merged: ScrollItem[] = [];
    for (const line of lines) {
      merged.push({ kind: 'line', key: `line:${line.id}`, createdAt: line.createdAt, line });
    }

    // decisions/078 follow-up — `data-widget-prompt-login` is special:
    // it carries stateful Cognito session info (set inside the widget
    // after `inlineSignIn` succeeds) that MUST persist across the model's
    // multiple emissions for one login flow (`submit_phone_for_login` then
    // `submit_otp_for_login`). If we gave each emission a unique React
    // key, the new component instance would mount with `session = null`
    // and `inlineVerifyOtp(null, otp)` would crash with
    // "Cannot read properties of null (reading 'sendCustomChallengeAnswer')"
    // (live repro on PetArmor `q3a405jcyr` 2026-05-21).
    //
    // Fix: render at most one `data-widget-prompt-login` row, keyed
    // stably on the type so React preserves the instance. The row's
    // chronological position is anchored to the FIRST emission's
    // `createdAt`; its `data` prop comes from the LATEST emission so
    // the displayed stage/prefill/confirmed flags are current.
    //
    // Other widget types (comparison / order-list / product-card / etc.)
    // keep per-event keys — successive emissions are independent
    // snapshots the user should be able to scroll back to.
    let firstPromptLoginAt: number | undefined;
    let latestPromptLoginEvent: (typeof widgetEvents)[number] | undefined;
    for (const event of widgetEvents) {
      if (event.type === 'data-widget-prompt-login') {
        if (firstPromptLoginAt === undefined) firstPromptLoginAt = event.createdAt;
        latestPromptLoginEvent = event;
      }
    }
    if (latestPromptLoginEvent && firstPromptLoginAt !== undefined) {
      merged.push({
        kind: 'widget',
        key: 'widget:data-widget-prompt-login',
        createdAt: firstPromptLoginAt,
        event: latestPromptLoginEvent,
      });
    }

    for (const event of widgetEvents) {
      if (event.type === 'data-widget-prompt-login') continue;
      merged.push({ kind: 'widget', key: `widget:${event.id}`, createdAt: event.createdAt, event });
    }
    merged.sort((a, b) => a.createdAt - b.createdAt);
    return merged;
  }, [lines, widgetEvents]);

  const statusText = pendingStatus?.trim();

  return (
    <Conversation
      style={{
        position: 'relative',
        flex: '1 1 auto',
        minHeight: 0,
        width: '100%',
        overflow: 'hidden',
        background: 'transparent',
      }}
    >
      <ConversationContent
        style={{
          // decisions/078 follow-up — generous top padding so the first
          // transcript bubble doesn't crowd the top edge of the overlay.
          // Extra bottom space so the last rows can scroll under the dock fade.
          padding: '48px 18px 130px',
        }}
      >
        {/* maxWidth matches the text-chat column (`MessageListV2` = 768) so
            federated widgets render at the same width in voice and text.
            `gap: 24` mirrors `MessageListV2`'s uniform inter-row spacing so the
            live overlay's message↔message and message↔widget rhythm matches the
            native text chat surface (rows no longer carry their own margins). */}
        <div
          style={{
            width: '100%',
            maxWidth: 768,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
          }}
        >
          {items.length === 0 ? (
            // Suppress the idle placeholder while a status row is showing (e.g.
            // the "Connecting…" shimmer before the call goes live), so the
            // shimmer reads as the active indicator on its own.
            statusText ? null : (
              <div
                style={{
                  color: 'var(--voice-header-fg, #9CA3AF)',
                  opacity: 0.5,
                  fontSize: 13,
                  fontStyle: 'italic',
                  textAlign: 'center',
                  paddingTop: 8,
                }}
              >
                Transcripts will appear here as you speak.
              </div>
            )
          ) : (
            items.map((item) =>
              item.kind === 'line' ? (
                <VoiceTranscriptBubble key={item.key} line={item.line} />
              ) : (
                <div key={item.key} style={{ width: '100%' }}>
                  <VoiceWidgetInTextChat
                    type={item.event.type}
                    data={item.event.data}
                    liveLoginCallbacks={liveLoginCallbacks}
                    collapseProductCardFeatures
                  />
                </div>
              ),
            )
          )}

          {statusText ? (
            <div style={{ width: '100%', padding: '4px 2px' }}>
              <Shimmer
                as="p"
                style={{ fontSize: 15, lineHeight: '22px', margin: 0, fontFamily: 'inherit' }}
                color="var(--voice-header-fg, #6B7280)"
                highlightColor="#FFFFFF"
              >
                {statusText}
              </Shimmer>
            </div>
          ) : null}
        </div>
      </ConversationContent>
      {/* Same desktop-only scroll-to-bottom arrow as the text chat
          (`ConciergeChatAreaV2`). When the blob dock is present it's raised
          above the 130px fade so it clears the blob; otherwise it keeps the
          text chat's default 16px offset. */}
      {!isCompact && <ConversationScrollButton className={hasBottomDock ? 'tw:bottom-[146px]' : undefined} />}
    </Conversation>
  );
};

export default VoiceScrollList;
